import React, { useState } from 'react';
import { SetupState, BuildingConfig, RoomListItem, DormType } from '../types';
import { provincesThai } from '../data/provinces';
import { 
  Building, 
  MapPin, 
  Users, 
  UserRoundCheck, 
  Sliders, 
  TextCursorInput, 
  Plus, 
  Sparkles, 
  Trash2, 
  AlertTriangle, 
  Check, 
  DoorClosed,
  Search,
  X
} from 'lucide-react';

interface Step1FormProps {
  state: SetupState;
  onChange: (updates: Partial<SetupState>) => void;
  errors: Record<string, string>;
}

export const Step1Form: React.FC<Step1FormProps> = ({ state, onChange, errors }) => {
  // Local state for auto-generator fields
  const [bldPrefix, setBldPrefix] = useState('A');
  const [bldFloors, setBldFloors] = useState<number | ''>(3);
  const [bldRoomsPerFloor, setBldRoomsPerFloor] = useState<number | ''>(8);
  const [bldFormat, setBldFormat] = useState<'prefix-floor-room' | 'floor-room' | 'slash'>('prefix-floor-room');

  // Room list live search and edit management states
  const [searchTerm, setSearchTerm] = useState('');
  const [customRoomName, setCustomRoomName] = useState('');
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const getFieldError = (field: string) => {
    if (errors[field]) return errors[field];
    if (!touched[field]) return null;

    if (field === 'dormName' && !state.dormName.trim()) return 'กรุณากรอกชื่อหอพักเพื่อแสดงความน่าเชื่อถือบนหัวบิล';
    if (field === 'province' && !state.province) return 'กรุณาเลือกจังหวัดของที่ตั้งหอพัก';
    if (field === 'rooms') {
      if (state.rooms.length === 0) return 'กรุณาเพิ่มห้องหรือสร้างตัวอย่างห้องพักอย่างน้อย 1 ห้อง';
      const roomNames = state.rooms.map(r => r.roomName);
      const duplicates = roomNames.filter((item, index) => roomNames.indexOf(item) !== index);
      if (duplicates.length > 0) return `พบเลขห้องซ้ำกันในระบบ (${Array.from(new Set(duplicates)).join(', ')})`;
      if (state.roomInputMethod === 'auto' && state.buildingsList.length === 0) return 'จำเป็นต้องกดปุ่ม "เพิ่มตึกนี้ลงตาราง" เพื่อประทับรับรองแผนผังตึกก่อน';
    }
    return null;
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleUpdateRoomName = (index: number, newName: string) => {
    const updated = [...state.rooms];
    updated[index] = { ...updated[index], roomName: newName };
    onChange({ rooms: updated });
  };

  const handleRemoveRoom = (index: number) => {
    const updated = state.rooms.filter((_, i) => i !== index);
    onChange({ rooms: updated });
  };

  const handleAddSingleRoom = (e: React.FormEvent) => {
    e.preventDefault();
    const name = customRoomName.trim();
    if (!name) return;
    onChange({
      rooms: [...state.rooms, { roomName: name, buildingId: null }]
    });
    setCustomRoomName('');
  };

  const handleClearAllRooms = () => {
    // Removed window.confirm due to iframe / browser sandbox blocks in standard outputs
    onChange({ rooms: [], manualRoomText: '', buildingsList: [] });
  };

  // Generator preview
  const getDemoRoomsList = (prefix: string, floors: number, numPerFloor: number, format: string) => {
    const list: string[] = [];
    const safeFloors = Math.max(1, floors);
    const safeNumPerFloor = Math.max(1, numPerFloor);
    for (let f = 1; f <= safeFloors; f++) {
      for (let r = 1; r <= safeNumPerFloor; r++) {
        const rstr = r < 10 ? `0${r}` : `${r}`;
        if (format === 'prefix-floor-room') {
          list.push(`${prefix}${f}${rstr}`);
        } else if (format === 'floor-room') {
          list.push(`${f}${rstr}`);
        } else if (format === 'slash') {
          list.push(prefix ? `${prefix}${f}/${r}` : `${f}/${r}`);
        }
      }
    }
    return list;
  };

  const demoRooms = getDemoRoomsList(
    bldPrefix.trim().toUpperCase(), 
    bldFloors === '' ? 1 : bldFloors, 
    bldRoomsPerFloor === '' ? 1 : bldRoomsPerFloor, 
    bldFormat
  );

  // Parse manual input helper
  const parseManualRooms = (text: string): RoomListItem[] => {
    if (!text) return [];
    // Split by commas, whitespace, or newlines
    const rawTokens = text.split(/[\n, ]+/);
    const uniqueRooms: RoomListItem[] = [];
    const seen = new Set<string>();

    rawTokens.forEach(token => {
      const cleaned = token.trim();
      if (cleaned) {
        // Find if room name is clean and non-duplicate
        uniqueRooms.push({ roomName: cleaned, buildingId: null });
      }
    });
    return uniqueRooms;
  };

  // Trigger whenever manual textarea changes
  const handleManualTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const parsed = parseManualRooms(text);
    onChange({
      manualRoomText: text,
      rooms: parsed
    });
  };

  // Generate rooms from active Method 1 settings and set to state
  const handleGenerateAutoRooms = () => {
    // Generate rooms list
    const roomsToSet: RoomListItem[] = [];
    state.buildingsList.forEach(config => {
      const generated = getDemoRoomsList(config.prefix, config.floors, config.roomsPerFloor, config.format);
      generated.forEach(name => {
        roomsToSet.push({
          roomName: name,
          buildingId: config.id,
          floor: undefined
        });
      });
    });

    // If buildings list is empty, generate from the current user inputs on screen
    if (state.buildingsList.length === 0) {
      const generated = getDemoRoomsList(
        bldPrefix.trim().toUpperCase(), 
        bldFloors === '' ? 3 : bldFloors, 
        bldRoomsPerFloor === '' ? 8 : bldRoomsPerFloor, 
        bldFormat
      );
      generated.forEach(name => {
        roomsToSet.push({ roomName: name, buildingId: 'temp' });
      });
    }

    onChange({ rooms: roomsToSet });
  };

  const handleAddBuildingAction = () => {
    const rawPrefix = bldPrefix.trim();
    const finalPrefix = rawPrefix ? rawPrefix.toUpperCase() : '';

    if (finalPrefix === '' && state.buildingsList.some(b => b.prefix === '')) {
      alert('มีกลุ่มห้องเลขล้วนอยู่แล้วในรายการ กรุณาระบุคำนำหน้าสำหรับตึกใหม่');
      return;
    }

    const finalFloors = bldFloors === '' ? 3 : bldFloors;
    const finalRooms = bldRoomsPerFloor === '' ? 8 : bldRoomsPerFloor;

    const newBld: BuildingConfig = {
      id: Date.now().toString(),
      prefix: finalPrefix,
      floors: finalFloors,
      roomsPerFloor: finalRooms,
      format: bldFormat,
    };
    const updatedBuildings = [...state.buildingsList, newBld];
    
    // Automatically generate rooms based on updated list of buildings
    const roomsToSet: RoomListItem[] = [];
    updatedBuildings.forEach(config => {
      const generated = getDemoRoomsList(config.prefix, config.floors, config.roomsPerFloor, config.format);
      generated.forEach(name => {
        roomsToSet.push({
          roomName: name,
          buildingId: config.id
        });
      });
    });

    onChange({
      buildingsList: updatedBuildings,
      rooms: roomsToSet
    });

    // Reset fields so user can type the next building comfortably (กรอกใหม่)
    setBldPrefix('');
    setBldFloors('');
    setBldRoomsPerFloor('');
  };

  const handleRemoveBuilding = (id: string) => {
    const updatedBuildings = state.buildingsList.filter(b => b.id !== id);
    // Regenerate
    const roomsToSet: RoomListItem[] = [];
    updatedBuildings.forEach(config => {
      const generated = getDemoRoomsList(config.prefix, config.floors, config.roomsPerFloor, config.format);
      generated.forEach(name => {
        roomsToSet.push({
          roomName: name,
          buildingId: config.id
        });
      });
    });

    onChange({
      buildingsList: updatedBuildings,
      rooms: roomsToSet
    });
  };

  // Find duplicates in rooms
  const roomNames = state.rooms.map(r => r.roomName);
  const duplicates = roomNames.filter((item, index) => roomNames.indexOf(item) !== index);
  const uniqueDuplicates = Array.from(new Set(duplicates));

  // Count rooms by prefix helper
  const getPrefixCountsStr = () => {
    const counts: Record<string, number> = {};
    state.rooms.forEach(room => {
      // Find pattern, e.g. letter before digit
      const match = room.roomName.match(/^([A-Za-z]+)/);
      const prefixKey = match ? match[1] : 'อื่น ๆ';
      counts[prefixKey] = (counts[prefixKey] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([prefix, count]) => `${prefix}: ${count} ห้อง`)
      .join(' | ');
  };

  return (
    <div className="space-y-6">
      
      {/* Title block */}
      <div className="border-b border-slate-100 pb-4">
        <h2 className="text-xl font-sans font-bold text-slate-800 flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-sm border border-amber-100 shrink-0 select-none">
            1
          </span>
          <span>เริ่มต้นตั้งค่าหอพักของคุณ</span>
        </h2>
        <p className="text-sm font-sans text-slate-500 mt-1 pl-10 leading-normal">
          กรอกข้อมูลพื้นฐานของหอพัก เพื่อใช้สร้างระบบบิลและหน้าเว็บสำหรับผู้เช่าของคุณ
        </p>
      </div>

      {/* Form Fields grids */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 font-sans">
        
        {/* Name input */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
            ชื่อหอพัก <span className="text-rose-500">*</span>
          </label>
          <div className="relative rounded-2xl shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
              <Building className="w-4 h-4" />
            </div>
            <input
              type="text"
              id="input-dorm-name"
              value={state.dormName}
              onChange={(e) => onChange({ dormName: e.target.value })}
              onBlur={() => handleBlur('dormName')}
              placeholder="ตัวอย่างเช่น สุขใจ อพาร์ทเมนต์"
              className={`block w-full pl-10 pr-3.5 py-3 text-sm rounded-2xl border ${
                getFieldError('dormName') ? 'border-rose-300 focus:ring-rose-200' : 'border-slate-200 focus:ring-indigo-100'
              } focus:outline-none focus:ring-4 transition duration-150 text-slate-800 font-medium bg-slate-50/50 focus:bg-white`}
            />
          </div>
          {getFieldError('dormName') && (
            <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {getFieldError('dormName')}
            </p>
          )}
        </div>

        {/* Location / Province selection */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
            จังหวัดที่ตั้งหอพัก <span className="text-rose-500">*</span>
          </label>
          <div className="relative rounded-2xl shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
              <MapPin className="w-4 h-4" />
            </div>
            <select
              id="input-province"
              value={state.province}
              onChange={(e) => onChange({ province: e.target.value })}
              onBlur={() => handleBlur('province')}
              className={`block w-full pl-10 pr-3.5 py-3 text-sm rounded-2xl border ${
                getFieldError('province') ? 'border-rose-300 focus:ring-rose-200' : 'border-slate-200 focus:ring-indigo-100'
              } focus:outline-none focus:ring-4 transition duration-150 text-slate-800 font-medium bg-slate-50/50 focus:bg-white`}
            >
              <option value="">-- เลือกจังหวัด --</option>
              {provincesThai.map((prov) => (
                <option key={prov} value={prov}>
                  {prov}
                </option>
              ))}
            </select>
          </div>
          {getFieldError('province') && (
            <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {getFieldError('province')}
            </p>
          )}
        </div>
      </div>

      {/* Type selection as Cards */}
      <div className="font-sans">
        <label className="block text-sm font-semibold text-slate-700 mb-2.5">
          ประเภทหอพัก <span className="text-rose-500">*</span>
        </label>
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          
          {[
            { id: 'mix', name: 'หอรวม', desc: 'ผู้ชายและผู้หญิงพักรวมกันได้', icon: Users, color: 'border-blue-200 text-blue-700 bg-blue-50/20 shadow-blue-50 focus-ring:ring-blue-100' },
            { id: 'male', name: 'หอพักชาย', desc: 'รับเฉพาะผู้เข้าพักชายเท่านั้น', icon: UserRoundCheck, color: 'border-teal-200 text-teal-700 bg-teal-50/20 shadow-teal-50 focus-ring:ring-teal-100' },
            { id: 'female', name: 'หอพักหญิง', desc: 'รับเฉพาะผู้เข้าพักหญิง ปลอดภัย', icon: UserRoundCheck, color: 'border-rose-200 text-rose-700 bg-rose-50/20 shadow-rose-50 focus-ring:ring-rose-100' }
          ].map((type) => {
            const Icon = type.icon;
            const isSelected = state.dormType === type.id;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => onChange({ dormType: type.id as DormType })}
                className={`relative p-3.5 rounded-2xl text-left border-2 flex flex-col items-center md:items-start text-xs md:text-sm transition duration-150 cursor-pointer ${
                  isSelected 
                    ? 'border-indigo-600 bg-indigo-50/50 text-indigo-900 shadow-md ring-4 ring-indigo-50' 
                    : 'border-slate-200 hover:border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                <div className={`p-2.5 rounded-xl mb-2.5 ${isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-150 text-slate-500'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="font-bold block tracking-wide">{type.name}</span>
                <span className="hidden md:block text-[11px] text-slate-400 mt-1 leading-normal">{type.desc}</span>
                
                {isSelected && (
                  <span className="absolute top-3.5 right-3.5 w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px]">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Buildings and Rooms Wizard Section */}
      <div className="font-sans border border-slate-100 rounded-3xl p-4 md:p-6 bg-slate-50/40">
        
        {/* Toggle entry method */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 border-b border-slate-100 pb-3">
          <h4 className="font-bold text-slate-800 text-sm md:text-base flex items-center gap-2">
            <DoorClosed className="w-4 h-4 text-indigo-500 shrink-0" />
            การตั้งค่าข้อมูล ตึก / ชั้น / ห้อง
          </h4>
          
          <div className="flex bg-slate-100 p-0.5 rounded-xl border border-slate-200/60 font-medium">
            <button
              type="button"
              className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition cursor-pointer ${
                state.roomInputMethod === 'auto'
                  ? 'bg-white text-slate-800 shadow-sm font-semibold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
              onClick={() => onChange({ roomInputMethod: 'auto' })}
            >
              <Sliders className="w-3.5 h-3.5" />
              <span>สร้างอัตโนมัติ</span>
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition cursor-pointer ${
                state.roomInputMethod === 'manual'
                  ? 'bg-white text-slate-800 shadow-sm font-semibold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
              onClick={() => onChange({ roomInputMethod: 'manual' })}
            >
              <TextCursorInput className="w-3.5 h-3.5" />
              <span>เขียนเลขห้องเอง</span>
            </button>
          </div>
        </div>

        {/* INPUT METHOD: AUTO GENERATE */}
        {state.roomInputMethod === 'auto' && (
          <div className="space-y-4">
            
            {/* Auto Inputs Widget */}
            <div className="bg-white border border-slate-100 p-4.5 rounded-2xl shadow-sm space-y-4">
              <span className="text-xs font-bold text-slate-400 block tracking-wider uppercase">
                ตั้งค่าตึกใหม่เพื่อคำนวณอัตโนมัติ
              </span>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                {/* Building Code Prefix */}
                <div>
                  <label className="block text-slate-500 font-semibold mb-1.5">รหัส/ชื่อตึก</label>
                  <input
                    type="text"
                    value={bldPrefix}
                    onChange={(e) => setBldPrefix(e.target.value.slice(0, 5))}
                    placeholder="เช่น A, B, ตึกเหนือ"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 shrink focus:outline-none focus:ring-2 focus:ring-indigo-100 text-slate-800 font-medium"
                  />
                  <p className="text-[10px] text-slate-400 mt-1 leading-snug">
                    เว้นว่างได้หากต้องการห้องเลขล้วน (เช่น 101) ระบบจะจัดเป็นกลุ่ม “ตึกหลัก”
                  </p>
                </div>
                {/* Floors count */}
                <div>
                  <label className="block text-slate-500 font-semibold mb-1.5">จำนวนชั้น (ชั้น)</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={bldFloors}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBldFloors(val === '' ? '' : Math.max(1, parseInt(val) || 1));
                    }}
                    placeholder="เช่น 3"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 text-slate-800 font-semibold"
                  />
                </div>
                {/* Rooms per floor */}
                <div>
                  <label className="block text-slate-500 font-semibold mb-1.5">ห้องต่อชั้น (ห้อง)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={bldRoomsPerFloor}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBldRoomsPerFloor(val === '' ? '' : Math.max(1, parseInt(val) || 1));
                    }}
                    placeholder="เช่น 8"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 text-slate-800 font-semibold"
                  />
                </div>
                {/* Formats */}
                <div>
                  <label className="block text-slate-500 font-semibold mb-1.5">รูปแบบเลขห้อง</label>
                  <select
                    value={bldFormat}
                    onChange={(e) => setBldFormat(e.target.value as any)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 text-slate-800 font-medium bg-white"
                  >
                    <option value="prefix-floor-room">ตึกชั้นห้อง (A101)</option>
                    <option value="floor-room">ชั้นห้องเพียวๆ (101)</option>
                    <option value="slash">ทับเศษ (A1/1)</option>
                  </select>
                </div>
              </div>

              {/* Live configuration draft preview */}
              <div className="bg-slate-50 rounded-xl p-3 text-xs leading-normal">
                <span className="text-slate-400 font-bold">ตัวอย่างเลขของตึกนี้:</span>
                {bldFloors === '' || bldRoomsPerFloor === '' ? (
                  <div className="text-slate-400 font-mono font-bold mt-1">
                    -
                  </div>
                ) : (
                  <div className="text-slate-600 font-mono font-medium truncate mt-1">
                    {demoRooms.slice(0, 10).join(', ')}
                    {demoRooms.length > 10 ? ' ...' : ''} 
                    <span className="text-amber-600 ml-1 font-semibold">({demoRooms.length} ห้อง)</span>
                  </div>
                )}
              </div>

              {/* Buttons Actions: Swapped order & styled "เพิ่มตึกนี้ลงตาราง" more prominent! */}
              <div className="flex flex-col sm:flex-row gap-2.5">
                <button
                  type="button"
                  onClick={handleGenerateAutoRooms}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 transition cursor-pointer order-2 sm:order-1"
                >
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span>สร้างตัวอย่างห้องทั้งหมด</span>
                </button>
                <button
                  type="button"
                  onClick={handleAddBuildingAction}
                  className="px-4 py-2.5 bg-amber-600 text-white hover:bg-amber-700 active:bg-amber-800 font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all shadow-md shadow-amber-100 cursor-pointer order-1 sm:order-2 flex-1"
                >
                  <Plus className="w-4 h-4 text-white" />
                  <span>เพิ่มตึกนี้ลงตาราง</span>
                </button>
              </div>
            </div>

            {/* List of configured buildings */}
            {state.buildingsList.length > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-bold text-slate-400 block tracking-wider uppercase">
                  ตึกที่เปิดใช้งานอยู่ ({state.buildingsList.length} ตึก)
                </span>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {state.buildingsList.map((bld) => (
                    <div 
                      key={bld.id} 
                      className="bg-white border border-slate-100 rounded-xl p-3.5 flex items-center justify-between text-xs transition hover:shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center font-bold text-sm border border-amber-100">
                          {bld.prefix}
                        </div>
                        <div>
                          <span className="font-bold text-slate-700">ตึก {bld.prefix}</span>
                          <span className="text-slate-450 block mt-0.5">
                            {bld.floors} ชั้น / ชั้นละ {bld.roomsPerFloor} ห้อง
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveBuilding(bld.id)}
                        className="text-slate-400 hover:text-rose-500 p-1.5 rounded-lg hover:bg-rose-50 transition cursor-pointer"
                        title="ลบตึกนี้"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}

        {/* INPUT METHOD: MANUAL TEXTAREA */}
        {state.roomInputMethod === 'manual' && (
          <div className="space-y-3">
            <div>
              <label className="block text-slate-500 font-semibold mb-1.5 text-xs">
                เขียนรายชื่อเลขห้องทั้งหมดของคุณ (ใช้ เว้นวรรค, ลูกน้ำ, หรือ ขึ้นบรรทัดใหม่ เพื่อแยกแต่ละห้อง)
              </label>
              <textarea
                value={state.manualRoomText}
                onChange={handleManualTextChange}
                placeholder="A101, A102, A103&#10;A201, A202&#10;B101, B102"
                rows={5}
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-indigo-100 text-slate-800 font-mono bg-white resize-y"
              />
            </div>
            <p className="text-[11px] text-slate-400 leading-normal">
              💡 แนะนำ: พิมพ์แยกเพื่อความเป็นระเบียบ เช่น บรรทัดละชั้นของห้อง ระบบจะประมวลผลเป็นชิปรายการให้อย่างสะดวกสบาย
            </p>
          </div>
        )}

        {/* ROOM LIST PREVIEW CHIPS & INTERACTIVE EDITOR */}
        <div className="mt-5 pt-5 border-t border-slate-200/60 font-sans">
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3.5">
            <div>
              <span className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                รายชื่อห้องและเครื่องมือจัดการจริง 
                <span className="px-2 py-0.5 rounded-full bg-slate-200/80 text-slate-700 text-xs font-mono font-bold">
                  {state.rooms.length} ห้อง
                </span>
              </span>
              {state.rooms.length > 0 && (
                <span className="text-xs text-slate-400 block mt-0.5">
                  แยกตามตึก: {getPrefixCountsStr() || 'ยังไม่มีการแบ่งตึก'}
                </span>
              )}
            </div>

            {/* Clear All action */}
            {state.rooms.length > 0 && (
              <button
                type="button"
                onClick={handleClearAllRooms}
                className="text-rose-600 hover:text-rose-700 text-xs font-bold transition inline-flex items-center gap-1 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>ล้างห้องทั้งหมด</span>
              </button>
            )}
          </div>

          {/* DUPLICATE WARNING */}
          {uniqueDuplicates.length > 0 && (
            <div className="bg-amber-50 text-amber-800 rounded-xl px-3 py-2.5 mb-4 border border-amber-200/70 text-xs flex items-start gap-2 leading-relaxed">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5 animate-bounce" />
              <div>
                <span className="font-bold">ตรวจสอบพบเลขห้องซ้ำในระบบ: </span>
                <span className="font-mono font-semibold">{uniqueDuplicates.join(', ')}</span> 
                <p className="mt-0.5 text-slate-500">กรุณาแก้ไขการกรอกข้อมูลไม่ให้เลขห้องซ้ำ เพื่อการจัดส่งบิลที่แม่นยำ</p>
              </div>
            </div>
          )}

          {/* INTERACTIVE CONTROLS: SEARCH & ADD SINGLE ROOM */}
          <div className="bg-slate-50 rounded-2xl p-3.5 border border-slate-150 mb-4 space-y-3">
            <div className="flex flex-col gap-2.5">
              {/* Live Search bar */}
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <Search className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="พิมพ์ค้นหาเลขห้องเพื่อแก้ไขหรือลบ..."
                  className="w-full pl-9 pr-8 py-2 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-100 rounded-xl text-xs text-slate-800 font-medium bg-white"
                />
                {searchTerm && (
                  <button 
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                    title="ล้างคำค้นหา"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Add Custom / Single Room */}
              <form onSubmit={handleAddSingleRoom} className="flex flex-col sm:flex-row gap-2 w-full">
                <input
                  type="text"
                  value={customRoomName}
                  onChange={(e) => setCustomRoomName(e.target.value)}
                  placeholder="เพิ่มเลขห้องเดี่ยว เช่น 101"
                  className="border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-100 text-slate-800 font-bold uppercase w-full sm:flex-1 bg-white"
                />
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 cursor-pointer shrink-0"
                >
                  <Plus className="w-4 h-4 shrink-0" />
                  <span>เพิ่มห้อง</span>
                </button>
              </form>
            </div>
            
            <p className="text-[10px] sm:text-[11px] text-slate-400 leading-normal whitespace-normal break-words">
              💡 วิธีใช้งาน: ค้นหาห้องที่ต้องการ จากนั้น<b>คลิกที่ช่องเลขห้องเพื่อพิมพ์แก้ไขเลขห้องได้ทันที</b> หรือกดปุ่ม <b>X</b> เพื่อลบออก สะดวกสบายทั้งคอมพิวเตอร์และจอมือถือ
            </p>
          </div>

          {/* EDITABLE GRID LIST AREA */}
          {state.rooms.length > 0 ? (
            <div>
              {/* Grid content */}
              {state.rooms.map((room, idx) => ({ room, idx })).filter(item => item.room.roomName.toLowerCase().includes(searchTerm.toLowerCase())).length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto p-2.5 bg-white rounded-2xl border border-slate-150 scrollbar-thin">
                  {state.rooms
                    .map((room, originalIdx) => ({ room, originalIdx }))
                    .filter(item => item.room.roomName.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map(({ room, originalIdx }) => (
                      <div 
                        key={originalIdx}
                        className={`px-2 py-1.5 rounded-xl border flex items-center justify-between gap-1 transition-all ${
                          uniqueDuplicates.includes(room.roomName)
                            ? 'bg-amber-50 border-amber-300'
                            : 'bg-slate-50/50 border-slate-150 hover:border-slate-300 hover:bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <DoorClosed className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                          <input
                            type="text"
                            value={room.roomName}
                            onChange={(e) => handleUpdateRoomName(originalIdx, e.target.value)}
                            className="w-full bg-transparent border-0 font-mono font-bold text-xs text-slate-800 focus:ring-0 focus:outline-none p-0 uppercase"
                            title="คลิกเพื่อพิมพ์แก้ไขเลขห้องโดยตรง"
                            placeholder="เลขห้อง"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveRoom(originalIdx)}
                          className="text-slate-400 hover:text-rose-600 p-1 rounded-lg hover:bg-rose-50 transition shrink-0 cursor-pointer"
                          title="ลบห้องนี้"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))
                  }
                </div>
              ) : (
                <div className="py-6 text-center rounded-2xl bg-slate-50 border border-slate-150 text-slate-400 text-xs">
                  <span>ไม่พบเลขห้องที่ตรงกับคำค้นหา: "{searchTerm}"</span>
                </div>
              )}
            </div>
          ) : (
            <div className="py-7 text-center rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 text-xs bg-white">
              <AlertTriangle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <span>ยังไม่มีข้อมูลห้องพิมพ์ในระบบ • กรุณากดปุ่ม "สร้างตัวอย่างห้องทั้งหมด" หรือระบุเลขห้องเอง</span>
            </div>
          )}

          {getFieldError('rooms') && (
            <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              {getFieldError('rooms')}
            </p>
          )}

        </div>

      </div>

    </div>
  );
};
